/**
 * BotFitters personalization.
 *
 * - Shows the garment total (selected variant + personalization, times quantity) above the button.
 * - prepare(): called by product-form.js right before an add-to-cart request. When the
 *   buyer filled in any paid field, it rewrites the request so the item and one
 *   "Personalization" fee line per paid field are added together, linked by a shared id.
 *   Each fee line uses the fee product's variant for that price (e.g. "$3.00") at the item's
 *   quantity; with no matching variant it falls back to the $0.25 variant x units.
 *     item:  properties[_bf_uid]
 *     fee:   properties[_bf_for], properties[_bf_units] (fee quantity per item),
 *            plus visible "For" and "<field>: <value>"
 *   Property names starting with "_" are hidden in the bag and at checkout.
 * - Either/or: fields sharing a data-bf-either group are "pick one"; filling one clears the others.
 * - Quantity changes and removals are kept in sync in component-cart-items.js.
 */
(function () {
  if (window.bfPersonalization) return;

  const UNIT_CENTS = 25;

  /** @param {number} cents */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /** @param {HTMLElement} fieldset */
  function paidFields(fieldset) {
    return /** @type {Array<HTMLInputElement|HTMLSelectElement>} */ (
      Array.from(fieldset.querySelectorAll('[data-bf-units]'))
    ).filter(
      (el) =>
        (!(el instanceof HTMLInputElement) || el.type !== 'radio' || el.checked) &&
        el.value.trim() !== '' &&
        Number(el.dataset.bfUnits) > 0
    );
  }

  /** @param {HTMLElement} fieldset */
  function updateTotal(fieldset) {
    const summary = fieldset.querySelector('[data-bf-summary]');
    if (!summary) return;
    const form = fieldset.closest('form');
    /** @type {Record<string, number>} */
    let prices = {};
    try {
      prices = JSON.parse(fieldset.dataset.variantPrices || '{}');
    } catch (e) {}
    const variantId = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'))?.value || '';
    const base = prices[variantId];
    const extra = paidFields(fieldset).reduce((sum, el) => sum + Number(el.dataset.bfUnits), 0) * UNIT_CENTS;
    const qtyInput = /** @type {HTMLInputElement | null} */ (
      // form.id would return the variant <input name="id">, so read the attribute
      form?.querySelector('input[name="quantity"]') ||
        document.querySelector(`input[name="quantity"][form="${form?.getAttribute('id')}"]`)
    );
    const quantity = Math.max(1, Number(qtyInput?.value) || 1);

    const set = (/** @type {string} */ sel, /** @type {string} */ text) => {
      const el = summary.querySelector(sel);
      if (el) el.textContent = text;
    };
    if (base !== undefined) set('[data-bf-base]', money(base));
    const extraRow = /** @type {HTMLElement | null} */ (summary.querySelector('[data-bf-extra-row]'));
    if (extraRow) extraRow.hidden = extra === 0;
    set('[data-bf-extra]', '+' + money(extra));
    set('[data-bf-grand-label]', quantity > 1 ? `Total for ${quantity}` : 'Total');
    if (base !== undefined) set('[data-bf-grand]', money((base + extra) * quantity));

    // The sticky Add to bag bar shows the same per-item price, personalization included
    const sticky = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__price');
    if (sticky && base !== undefined) sticky.textContent = money(base + extra);
  }

  function updateAll() {
    document.querySelectorAll('[data-bf-personalization]').forEach((el) => updateTotal(/** @type {HTMLElement} */ (el)));
  }

  /**
   * Either/or: when a field in a data-bf-either group gets a value, clear the rest of the group
   * (text and dropdowns emptied, picture tiles back to None).
   * @param {HTMLElement} fieldset
   * @param {HTMLElement} target
   */
  function clearEitherOr(fieldset, target) {
    const group = target.dataset.bfEither;
    if (!group || !(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    const filled = target instanceof HTMLInputElement && target.type === 'radio' ? target.checked && target.value !== '' : target.value.trim() !== '';
    if (!filled) return;
    fieldset.querySelectorAll(`[data-bf-either="${CSS.escape(group)}"]`).forEach((el) => {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement) || el.name === target.name) return;
      if (el instanceof HTMLInputElement && el.type === 'radio') el.checked = el.value === '';
      else el.value = '';
    });
  }

  document.addEventListener('input', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const fieldset = target.closest?.('[data-bf-personalization]');
    if (!fieldset) return;
    clearEitherOr(/** @type {HTMLElement} */ (fieldset), target);
    if (target.matches('[inputmode="numeric"]')) {
      /** @type {HTMLInputElement} */ (target).value = /** @type {HTMLInputElement} */ (target).value.replace(/\D/g, '');
    }
    updateTotal(/** @type {HTMLElement} */ (fieldset));
  });
  document.addEventListener('change', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const fieldset = /** @type {HTMLElement} */ (target.closest?.('[data-bf-personalization]'));
    if (!fieldset) return;
    clearEitherOr(fieldset, target);
    updateTotal(fieldset);
  });

  /**
   * @param {HTMLFormElement} form
   * @param {FormData} formData - the request Horizon is about to send
   * @returns {FormData | null} a replacement body, or null to send formData unchanged
   */
  function prepare(form, formData) {
    const fieldset = form.querySelector('[data-bf-personalization]');
    if (!(fieldset instanceof HTMLElement)) return null;

    // Names print in capitals; store them that way too.
    fieldset.querySelectorAll('input[data-bf-upper]').forEach((el) => {
      /** @type {HTMLInputElement} */ (el).value = /** @type {HTMLInputElement} */ (el).value.toUpperCase();
    });
    const upper = new Set(
      Array.from(fieldset.querySelectorAll('input[data-bf-upper]')).map((el) => /** @type {HTMLInputElement} */ (el).name)
    );

    const paid = paidFields(fieldset);
    /** @type {Record<string, number>} fee variant id by price in cents */
    let feeVariants = {};
    try {
      feeVariants = JSON.parse(fieldset.dataset.feeVariants || '{}');
    } catch (e) {}
    const fallbackVariant = feeVariants[String(UNIT_CENTS)] || fieldset.dataset.feeVariant;
    if (paid.length === 0 || !fallbackVariant) {
      upper.forEach((name) => {
        const v = formData.get(name);
        if (typeof v === 'string') formData.set(name, v.toUpperCase());
      });
      // Skip fields left empty (or tiles on None) so the order only lists what was chosen
      for (const [key, value] of [...formData.entries()]) {
        if (key.startsWith('properties[') && typeof value === 'string' && value.trim() === '') formData.delete(key);
      }
      return null;
    }

    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
    const uid = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

    // The cart lists lines added together in reverse order of the request, so the fee
    // lines are written first (last field first) and the item last: the bag then shows the
    // item followed by its fees in field order.
    const body = new FormData();
    [...paid].reverse().forEach((el, i) => {
      // One fee line per paid field, e.g. "Back name: WRIGHT" at $5.00 x the item's quantity.
      const units = Number(el.dataset.bfUnits);
      const exact = feeVariants[String(units * UNIT_CENTS)];
      const perItem = exact ? 1 : units;
      const n = `items[${i}]`;
      const value = upper.has(el.name) ? el.value.toUpperCase() : el.value;
      body.set(`${n}[id]`, String(exact || fallbackVariant));
      body.set(`${n}[quantity]`, String(perItem * quantity));
      body.set(`${n}[properties][For]`, fieldset.dataset.productTitle || '');
      body.set(`${n}[properties][${el.dataset.bfLabel || 'Personalization'}]`, value);
      body.set(`${n}[properties][_bf_for]`, uid);
      body.set(`${n}[properties][_bf_units]`, String(perItem));
    });

    const item = `items[${paid.length}]`;
    for (const [key, value] of formData.entries()) {
      if (key === 'id') body.set(`${item}[id]`, value);
      else if (key === 'quantity') continue;
      else if (key.startsWith('properties[')) {
        const v = typeof value === 'string' && upper.has(key) ? value.toUpperCase() : value;
        // properties[Back name] -> items[n][properties][Back name]
        if (v !== '') body.set(`${item}[` + key.replace('[', ']['), v);
      }
    }
    body.set(`${item}[quantity]`, String(quantity));
    body.set(`${item}[properties][_bf_uid]`, uid);
    for (const key of ['sections', 'sections_url']) {
      const v = formData.get(key);
      if (v !== null) body.set(key, v);
    }

    return body;
  }

  // Quantity chosen on a product card ("Customize" adds ?quantity=N to the link)
  function applyQuantityFromUrl() {
    const qty = Math.min(99, Math.max(1, Number(new URL(window.location.href).searchParams.get('quantity')) || 0));
    if (qty < 2) return;
    const input = /** @type {HTMLInputElement | null} */ (
      document.querySelector('[data-bf-personalization]')?.closest('form')?.querySelector('input[name="quantity"]')
    );
    if (!input) return;
    input.value = String(qty);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyQuantityFromUrl);
  else applyQuantityFromUrl();

  // Size/color changes (product-form.js) and quantity changes update the total too.
  // (again a moment later, after the section re-render refreshes the sticky bar's price)
  document.addEventListener('bf:variant-change', () => {
    updateAll();
    setTimeout(updateAll, 150);
  });
  document.addEventListener('quantity-selector:update', () => setTimeout(updateAll));
  document.addEventListener('change', (event) => {
    if (/** @type {HTMLElement} */ (event.target).matches?.('input[name="quantity"]')) updateAll();
  });
  document.addEventListener('click', (event) => {
    if (/** @type {HTMLElement} */ (event.target).closest?.('quantity-selector-component button, .quantity-selector button')) setTimeout(updateAll);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', updateAll);
  else updateAll();

  window.bfPersonalization = { prepare, UNIT_CENTS };
})();
